import React, { useRef, useEffect, useState } from 'react';
import { DrawingTool } from '../types';

interface DrawingCanvasProps {
  width: number;
  height: number;
  isDrawingEnabled: boolean;
  onDrawingChange: (dataUrl: string | null) => void;
  clearSignal: number;
  undoSignal: number;
  strokeColor: string;
  tool: DrawingTool;
}

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({
  width, height, isDrawingEnabled, onDrawingChange,
  clearSignal, undoSignal, strokeColor, tool
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);

  const startPos = useRef<{ x: number; y: number } | null>(null);
  const snapshot = useRef<ImageData | null>(null);
  const history = useRef<ImageData[]>([]);
  const historyIndex = useRef(-1);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (context) {
        setCtx(context);
      }
    }
  }, []);

  const saveState = () => {
    if (!ctx || !canvasRef.current) return;
    if (historyIndex.current < history.current.length - 1) {
      history.current.splice(historyIndex.current + 1);
    }
    const imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
    history.current.push(imageData);
    historyIndex.current = history.current.length - 1;
  };

  useEffect(() => {
    if (undoSignal > 0 && ctx && canvasRef.current) {
      if (historyIndex.current > 0) {
        historyIndex.current -= 1;
        const prevState = history.current[historyIndex.current];
        ctx.putImageData(prevState, 0, 0);
        onDrawingChange(canvasRef.current.toDataURL('image/webp', 0.8));
      } else if (historyIndex.current === 0) {
        historyIndex.current = -1;
        ctx.clearRect(0, 0, width, height);
        history.current = [];
        onDrawingChange(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undoSignal]);

  useEffect(() => {
    if (clearSignal > 0 && ctx) {
      ctx.clearRect(0, 0, width, height);
      history.current = [];
      historyIndex.current = -1;
      onDrawingChange(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSignal]);

  const startDrawing = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingEnabled || !ctx || !canvasRef.current) return;
    const { offsetX, offsetY } = event.nativeEvent;

    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = tool === 'eraser' ? 20 : 5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = tool === 'eraser' ? 'destination-out' : 'source-over';

    setIsDrawing(true);
    startPos.current = { x: offsetX, y: offsetY };
    snapshot.current = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);

    if (tool === 'pencil' || tool === 'eraser' || tool === 'freehand') {
      ctx.beginPath();
      ctx.moveTo(offsetX, offsetY);
    }
  };

  const draw = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !isDrawingEnabled || !ctx) return;
    const { offsetX, offsetY } = event.nativeEvent;

    if (tool === 'pencil' || tool === 'eraser' || tool === 'freehand') {
      ctx.lineTo(offsetX, offsetY);
      ctx.stroke();
      return;
    }

    if (!snapshot.current || !startPos.current) return;

    ctx.putImageData(snapshot.current, 0, 0);
    ctx.beginPath();

    switch (tool) {
      case 'line':
        ctx.moveTo(startPos.current.x, startPos.current.y);
        ctx.lineTo(offsetX, offsetY);
        break;
      case 'square':
        ctx.rect(startPos.current.x, startPos.current.y, offsetX - startPos.current.x, offsetY - startPos.current.y);
        break;
      case 'circle': {
        const radiusX = Math.abs(offsetX - startPos.current.x) / 2;
        const radiusY = Math.abs(offsetY - startPos.current.y) / 2;
        const centerX = (startPos.current.x + offsetX) / 2;
        const centerY = (startPos.current.y + offsetY) / 2;
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
        break;
      }
      default:
        break;
    }
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing || !ctx || !canvasRef.current) return;
    setIsDrawing(false);

    saveState();
    onDrawingChange(canvasRef.current.toDataURL('image/webp', 0.8));
    startPos.current = null;
    snapshot.current = null;
  };

  const canvasClassName = isDrawingEnabled
    ? 'absolute top-0 left-0 cursor-crosshair z-20'
    : 'absolute top-0 left-0 pointer-events-none z-20';

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={canvasClassName}
      style={{
        transform: 'translateZ(0)',
      }}
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseLeave={stopDrawing}
    />
  );
};

export default DrawingCanvas;
